import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Box, Button, Checkbox, Chip, CircularProgress, FormControlLabel, Grid, IconButton,
  MenuItem, Stack, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import { DataGridPro, GridToolbar } from '@mui/x-data-grid-pro';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LaunchIcon from '@mui/icons-material/Launch';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ErrorIcon from '@mui/icons-material/Error';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { toast } from 'react-toastify';
import axios from 'axios';

const api = axios.create({ withCredentials: true });
const BASE = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_BASEURL) || '';

const STATUS_COLORS = {
  received: 'default',
  parsed: 'info',
  mapped: 'info',
  needs_review: 'warning',
  submitted: 'success',
  failed: 'error',
  dismissed: 'default',
  dismissed_duplicate: 'default',
  submit_uncertain: 'warning',
};

const STATUS_LABEL = {
  received: 'Received',
  parsed: 'Parsed',
  mapped: 'Mapped',
  needs_review: 'Needs Review',
  submitted: 'Submitted',
  failed: 'Failed',
  dismissed: 'Dismissed',
  dismissed_duplicate: 'Duplicate',
  submit_uncertain: 'Uncertain',
};

function StatusChip({ status }) {
  return <Chip size="small" label={STATUS_LABEL[status] || status} color={STATUS_COLORS[status] || 'default'} />;
}

function SpacexQueue({ onOpen, refreshKey }) {
  const { getAccessTokenSilently } = useAuth0();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [counts, setCounts] = useState({});  // { status: n }

  const FILTER_BUCKETS = useMemo(() => ({
    all:       null,
    open:      ['received','parsed','mapped','needs_review','submit_uncertain','failed'],
    review:    ['needs_review'],
    failed:    ['failed'],
    submitted: ['submitted'],
    dismissed: ['dismissed','dismissed_duplicate'],
  }), []);

  const statusToQuery = useCallback((s) => {
    const list = FILTER_BUCKETS[s];
    return list ? list.join(',') : null;
  }, [FILTER_BUCKETS]);

  const countFor = useCallback((bucket) => {
    const list = FILTER_BUCKETS[bucket];
    if (list === null) {
      return Object.values(counts).reduce((a, b) => a + b, 0);
    }
    return list.reduce((a, s) => a + (counts[s] || 0), 0);
  }, [FILTER_BUCKETS, counts]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getAccessTokenSilently({
        audience: process.env.REACT_APP_AUTH0AUDIENCE,
        scope: process.env.REACT_APP_AUTH0SCOPE,
      });
      const params = { limit: 200 };
      const sq = statusToQuery(statusFilter);
      if (sq) params.status = sq;
      const headers = { Authorization: `Bearer ${token}` };
      const [jobsRes, statsRes] = await Promise.all([
        api.get(`${BASE}/api/spacex/jobs`, { params, headers }),
        api.get(`${BASE}/api/spacex/stats`, { headers }),
      ]);
      setRows((jobsRes.data.jobs || []).map((j) => ({ id: j.job_id, ...j })));
      setTotal(jobsRes.data.total || 0);
      const next = {};
      for (const c of (statsRes.data.counts || [])) next[c.status] = c.n;
      setCounts(next);
    } catch (e) {
      toast.error('Failed to load jobs: ' + (e.response?.data?.error || e.response?.data || e.message));
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, statusFilter, statusToQuery]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const FilterButton = ({ value, label }) => {
    const n = countFor(value);
    return (
      <ToggleButton value={value} sx={{ textTransform: 'none', px: 1.5 }}>
        {label}
        <Chip
          size="small"
          label={n}
          sx={{
            ml: 1, height: 20, fontSize: '0.7rem',
            bgcolor: statusFilter === value ? 'primary.main' : 'grey.300',
            color:   statusFilter === value ? 'primary.contrastText' : 'text.primary',
            '& .MuiChip-label': { px: 0.75 },
          }}
        />
      </ToggleButton>
    );
  };

  const columns = useMemo(() => [
    { field: 'actions', headerName: '', width: 100, sortable: false, filterable: false,
      renderCell: (p) => (
        <Button size="small" variant="outlined" onClick={() => onOpen(p.row.id)}>Open</Button>
      ),
    },
    { field: 'received_at', headerName: 'Received', width: 180,
      renderCell: (p) => p.value ? new Date(p.value).toLocaleString() : '' },
    { field: 'file_name', headerName: 'File', flex: 1.5, minWidth: 240 },
    { field: 'file_type', headerName: 'Type', width: 70 },
    { field: 'status', headerName: 'Status', width: 140,
      renderCell: (p) => <StatusChip status={p.value} /> },
    { field: 'confidence', headerName: 'Confidence', width: 120,
      renderCell: (p) => p.value == null ? '—' : (p.value * 100).toFixed(0) + '%' },
    { field: 'tai_shipment_id', headerName: 'TAI Shipment', width: 160,
      renderCell: (p) => p.value ? (
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography variant="body2">{p.value}</Typography>
          <Tooltip title="Open in TAI"><IconButton size="small" href={`https://freightsaver.taicloud.net/back-office/shipment/shipment-details/${p.value}`} target="_blank"><LaunchIcon fontSize="inherit" /></IconButton></Tooltip>
        </Stack>
      ) : '—'
    },
    { field: 'from_address', headerName: 'From', flex: 1, minWidth: 180 },
    { field: 'error_code', headerName: 'Error', width: 160 },
  ], [onOpen]);

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, width: '100%', overflow: 'hidden', p: 2, boxSizing: 'border-box' }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2, flexWrap: 'wrap', gap: 1, flexShrink: 0 }}>
        <Typography variant="h5">SpaceX Intake</Typography>
        <ToggleButtonGroup
          size="small"
          value={statusFilter}
          exclusive
          onChange={(_, v) => v && setStatusFilter(v)}
        >
          <FilterButton value="all"       label="All" />
          <FilterButton value="open"      label="Open" />
          <FilterButton value="review"    label="Needs Review" />
          <FilterButton value="failed"    label="Failed" />
          <FilterButton value="submitted" label="Submitted" />
          <FilterButton value="dismissed" label="Dismissed" />
        </ToggleButtonGroup>
        <Box sx={{ flex: 1 }} />
        <Typography variant="body2" color="text.secondary">{total} shown</Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={load}><RefreshIcon /></IconButton>
        </Tooltip>
      </Stack>
      <Box sx={{ flex: 1, minHeight: 0, width: '100%', overflow: 'hidden', position: 'relative' }}>
        <DataGridPro
          rows={rows}
          columns={columns}
          loading={loading}
          density="compact"
          slots={{ toolbar: GridToolbar }}
          disableRowSelectionOnClick
          initialState={{ sorting: { sortModel: [{ field: 'received_at', sort: 'desc' }] } }}
          sx={{
            position: 'absolute', inset: 0,
            border: 1, borderColor: 'divider',
            '& .MuiDataGrid-virtualScroller': { minHeight: 0 },
          }}
        />
      </Box>
    </Box>
  );
}

function PrettyJSON({ data }) {
  return (
    <Box component="pre" sx={{
      m: 0, p: 1.5, bgcolor: 'grey.100', borderRadius: 1, fontSize: '0.78rem',
      maxHeight: 380, overflow: 'auto', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
    }}>
      {data == null ? 'null' : (typeof data === 'string' ? data : JSON.stringify(data, null, 2))}
    </Box>
  );
}

// --- Editable field helpers ----------------------------------------------------------------
// Enum lists below are pulled verbatim from the TAI Public API V2 swagger
// (instructions/tai_phase0/tai_publicapi_v2_swagger.json).

const SHIPMENT_TYPES = [
  'Truckload', 'LTL', 'Domestic Freight', 'Small Package', 'Cartage', 'Drayage',
  'International Air', 'International Ocean', 'Combined International Air', 'Combined International Ocean',
  'Supplemental Insurance Provider', 'Other',
];

const TRAILER_TYPES = [
  '53 ft Van | Dry','None','Other','Flatbed','Refrigerated','Van',
  'Sprinter Van | Dry','Sprinter Van | Heat','Sprinter Van | Reefer',
  '24 ft City Truck w/ Liftgate | Dry','24 ft City Truck w/ Liftgate | Heat','24 ft City Truck w/ Liftgate | Reefer',
  '28 ft City Truck w/ Liftgate | Dry','28 ft City Truck w/ Liftgate | Heat','28 ft City Truck w/ Liftgate | Reefer',
  '40 ft Straight Truck w/ Tailgate | Dry','40 ft Straight Truck w/ Tailgate | Heat','40 ft Straight Truck w/ Tailgate | Reefer',
  '48 ft Van | Dry','48 ft Van | Heat','48 ft Van | Reefer',
  '48 ft Van w/ Tailgate | Dry','48 ft Van w/ Tailgate | Heat','48 ft Van w/ Tailgate | Reefer',
  '53 ft Van | Dry Tandem','53 ft Van | Dry Tandem w/ Liftgate','53 ft Van | Dry Tandem Heat','53 ft Van | Dry Tandem Reefer',
  '53 ft Van | Dry Tri-Axle','53 ft Van | Heat Tri-Axle','53 ft Van | Reefer Tri-Axle',
  '53 ft Van | Dry Quad-Axle','53 ft Van | Heat Quad-Axle','53 ft Van | Reefer Quad-Axle',
  '48 ft Flatbed','48 ft Conestoga / Curtain Side','53 ft Flatbed','53 ft Conestoga / Curtain Side',
  '53 ft Super B','53 ft Double Drop','Low Boy / RG','Power Only','53 ft Van | Plate Trailer',
  '48 ft Step Deck','53 ft Step Deck','53 ft Van | Reefer',
  'Container - Standard','Container - Heavy','Container - OOG','Container - Intermodal','Container - High Cube',
  '26 ft Straight Truck','Van | Flatbed','50 ft Flatbed','50 ft Van | Dry','53 ft Van | Air Ride',
  '32 ft HotShot','36 ft HotShot','40 ft HotShot','24 ft Straight Truck','12 ft Straight Truck',
  '48 ft Low Pro Step Deck','53 ft Low Pro Step Deck',
  '48 ft Step Deck | Conestoga','53 ft Step Deck | Conestoga',
  '48 ft Flatbed | Conestoga','53 ft Flatbed | Conestoga',
  'Open Car Carrier','Enclosed Car Carrier',
  'Tanker | Aluminum','Tanker | Intermodal','Tanker | Steel','Tanker | Fiberglass','Tanker | Stainless Steel',
  'Tanker | Kosher','Tanker | Multi-Compartment','Tanker | Plastite-Lined','Tanker | Pneumatic','Tanker | Rubber-Lined',
  '48 ft Dry Van | Reefer','53 ft Dry Van | Reefer','53 ft Reefer',
  'Hopper','Dump','48 ft Flatbed | Tarp','50 ft Flatbed | Tarp','53 ft Flatbed | Tarp',
  'Livestock','Vented | Van','Toter','Container | Flexibag',
  'Flatbed | Dry Tri-Axle','Flatbed | Reefer Tri-Axle','Flatbed | Maxi','Landoll',
];

const TRAILER_SIZES = ['Full', 'Partial'];

const SERVICE_LEVELS = [
  'Standard','Normal','Standard Truckload','Partial','Team Service','Drayage','Intermodal',
  'Accelerated','Time-Critical','Premium','Friday Only Pickup',
  'White Glove Delivery','White Glove Pickup','White Glove','White Glove Threshold','White Glove Threshold 2nd Floor',
  'White Glove Room of Choice','White Glove Outside','White Glove Assembly','White Glove Unattended',
  'LTL Reefer Frozen','LTL Reefer Chilled','General','Air Express','Charter','Courier',
  'Transit: Next Day','Transit: 2nd Day','Transit: 3 to 5 Days','Transit: Deferred',
  'Guaranteed','Guaranteed Next Day','Guaranteed End of Day','Guaranteed By Morning','Guaranteed By Noon',
  'Guaranteed By 2PM','Guaranteed By 3PM','Guaranteed By 3:30PM','Guaranteed By 5PM','Guaranteed Hourly Window',
  'Guaranteed 4 Business Days','Guaranteed Exclusive Use','Urgent Care Arrival Day Guaranteed',
  'TCSA','TCSP','TCSW','Hand Carry','Next Flight Out','Rail','TST Guaranteed',
  'Puerto Rico Priority','Puerto Rico Standard','Same Day','Next Day','Next Day by Noon',
  '2nd Day','3 to 5 Day Deferred','Deferred','Exclusive Use','Parcel','Tradeshow Only',
  'Priority Overnight','Priority Noon','Standard Overnight','First Overnight','Two Day AM','Two Day',
  '1Day Freight','2Day Freight','3Day Freight','First Freight','Express Saver',
  'Ground','Home Delivery','Smart Post',
  'Volume','Volume: Guaranteed Standard','Volume: Guaranteed Economy','Volume: Guaranteed Exclusive Use',
  'Volume: Truckload Exempt','Volume: Truckload Service','Volume: Exclusive Use',
  'Economy','Priority','Auto','Threshold','Oversize - Permits','Cross Dock',
];

const WEIGHT_UNITS    = ['lbs', 'kg'];
const DIMENSION_UNITS = ['in', 'cm', 'ft', 'm'];

const PACKAGING_TYPES = [
  'Pallet','Box','Skid','Carton','Crate','Drum','Bundle','Roll','Container','Stacks',
  'Bags','Pails','Totes','Wheels','Gaylords','U-Box','HalfPallets','Cart','A-Frame',
  'Hopper','Vehicle','Tubs','Gallons','Bales','Cans','Jerricans','Racks','Loose','Bins','Batteries',
  'Barrel','Basket','Break Bulk','Case','Chest','Cylinder','Lift Van','Package','Piece','Reel',
  'Truckload','Tube','Unit',
];

const FREIGHT_CLASSES = [
  'No Class','50','55','60','65','70','77.5','85','92.5','100','110','125','150','175','200','250','300','400','500',
];

const COUNTRIES = [
  'USA','Canada','Mexico','United Kingdom','Germany','France','Italy','Spain','Netherlands','Belgium',
  'Switzerland','Austria','Ireland','Sweden','Norway','Denmark','Finland','Poland','Portugal','Greece',
  'Turkey','China','Japan','South Korea','Taiwan','Hong Kong','Singapore','Australia','New Zealand',
  'India','Pakistan','Israel','United Arab Emirates','Saudi Arabia','Brazil','Argentina','Chile','Colombia',
  'Peru','South Africa','Other',
];

// US states — TAI accepts free text but a dropdown reduces typos. Includes territories/military.
const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC','PR','VI','GU','AS','AP','AA','AE',
];

const COMMON_REF_TYPES = [
  'Customer PO Number', 'Shipper Reference Number',
  'Custom Reference Number', 'Custom Reference Number 2', 'Custom Reference Number 3',
  'Load Release Number', 'Container Number', 'Quote Number', 'File Number',
  'Driver Cell Phone Number', 'Driver Name', 'Trailer Number', 'Truck Number',
  'Carrier Dispatcher Name', 'Carrier Dispatcher Phone Number', 'Carrier Dispatcher Email',
  'Carrier Rep', 'Linehaul Carrier Pro Number', 'Pickup Carrier Pro Number', 'Delivery Carrier Pro Number',
  'Seal Number', 'MAWB Number', 'Temperature', 'Insurance Reference Number',
];

// Visual treatment applied to every editable field (TextField, Select, etc.) when the
// surrounding context is read-only (closed job or in-flight submission). Goal: it looks
// obviously locked but the values stay legible.
const READ_ONLY_SX = {
  '& .MuiInputBase-root': {
    bgcolor: 'grey.100',
    cursor: 'default',
    '& fieldset':                  { borderColor: 'divider' },
    '&:hover fieldset':            { borderColor: 'divider' },
    '&.Mui-focused fieldset':      { borderColor: 'divider', borderWidth: '1px' },
  },
  '& .MuiInputBase-input':         { cursor: 'default' },
  // Keep text legible even when MUI greys out a disabled select / input
  '& .MuiInputBase-input.Mui-disabled':  { WebkitTextFillColor: 'rgba(0,0,0,0.85)', cursor: 'default' },
  '& .MuiSelect-select.Mui-disabled':    { WebkitTextFillColor: 'rgba(0,0,0,0.85)', cursor: 'default' },
  // Hide the dropdown chevron on disabled selects so they stop looking like dropdowns
  '& .MuiSelect-icon.Mui-disabled':      { color: 'transparent' },
};

// Trim ISO datetime to "YYYY-MM-DDTHH:MM" suitable for <input type="datetime-local">
function isoToLocalInput(s) {
  if (!s) return '';
  // s is like "2026-04-17T08:00:00-05:00" — keep year-month-day-time, drop seconds and offset
  const m = String(s).match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/);
  return m ? `${m[1]}T${m[2]}` : '';
}
// Restore ISO with offset; preserve original offset if present, else use local browser offset
function localInputToIso(local, originalIso) {
  if (!local) return null;
  let offset = '+00:00';
  if (originalIso) {
    const m = String(originalIso).match(/([+-]\d{2}:\d{2})$/);
    if (m) offset = m[1];
  }
  return `${local}:00${offset}`;
}

function EditField({ label, value, onChange, original, multiline = false, rows = 1, type = 'text', readOnly = false, sx, ...rest }) {
  const dirty = !readOnly && (value || '') !== (original || '');
  const baseSx = readOnly ? READ_ONLY_SX : (
    dirty
      ? { '& .MuiInputBase-root': { bgcolor: 'warning.50', boxShadow: '0 0 0 1px rgba(237,108,2,0.4) inset' } }
      : {}
  );
  return (
    <TextField
      size="small"
      fullWidth
      label={label}
      value={value ?? ''}
      onChange={readOnly ? undefined : (e) => onChange(e.target.value)}
      multiline={multiline}
      rows={rows}
      type={type}
      InputLabelProps={type === 'datetime-local' ? { shrink: true } : undefined}
      InputProps={readOnly ? { readOnly: true } : undefined}
      sx={{ ...baseSx, ...sx }}
      {...rest}
    />
  );
}

function StopEditor({ stop, original, onChange, title, readOnly = false }) {
  const set = (k, v) => onChange({ ...stop, [k]: v });
  return (
    <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, minWidth: 0 }}>
      <Typography variant="overline" color="primary" sx={{ fontWeight: 700 }}>{title}</Typography>
      <Grid container spacing={1.5} sx={{ mt: 0 }}>
        <Grid item xs={12}>
          <EditField label="Company name" value={stop.companyName} original={original?.companyName} onChange={(v) => set('companyName', v)} readOnly={readOnly} />
        </Grid>
        <Grid item xs={12}>
          <EditField label="Street address" value={stop.streetAddress} original={original?.streetAddress} onChange={(v) => set('streetAddress', v)} readOnly={readOnly} />
        </Grid>
        <Grid item xs={12}>
          <EditField label="Street address (line 2)" value={stop.streetAddressTwo} original={original?.streetAddressTwo} onChange={(v) => set('streetAddressTwo', v)} readOnly={readOnly} />
        </Grid>
        <Grid item xs={12} sm={5}>
          <EditField label="City" value={stop.city} original={original?.city} onChange={(v) => set('city', v)} readOnly={readOnly} />
        </Grid>
        <Grid item xs={4} sm={2}>
          <TextField select size="small" fullWidth label="State" value={stop.state || ''}
            onChange={readOnly ? undefined : (ev) => set('state', ev.target.value)}
            disabled={readOnly} sx={readOnly ? READ_ONLY_SX : undefined}>
            <MenuItem value="">—</MenuItem>
            {US_STATES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            {stop.state && !US_STATES.includes(stop.state) && (
              <MenuItem value={stop.state}>{stop.state}</MenuItem>
            )}
          </TextField>
        </Grid>
        <Grid item xs={5} sm={3}>
          <EditField label="Zip" value={stop.zipCode} original={original?.zipCode} onChange={(v) => set('zipCode', v)} readOnly={readOnly} />
        </Grid>
        <Grid item xs={3} sm={2}>
          <TextField select size="small" fullWidth label="Country" value={stop.country || 'USA'}
            onChange={readOnly ? undefined : (ev) => set('country', ev.target.value)}
            disabled={readOnly} sx={readOnly ? READ_ONLY_SX : undefined}>
            {COUNTRIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            {stop.country && !COUNTRIES.includes(stop.country) && (
              <MenuItem value={stop.country}>{stop.country}</MenuItem>
            )}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6}>
          <EditField label="Contact name" value={stop.contactName} original={original?.contactName} onChange={(v) => set('contactName', v)} readOnly={readOnly} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <EditField label="Phone" value={stop.phone} original={original?.phone} onChange={(v) => set('phone', v)} readOnly={readOnly} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <EditField label="Ready" type="datetime-local"
            value={isoToLocalInput(stop.estimatedReadyDateTime)}
            original={isoToLocalInput(original?.estimatedReadyDateTime)}
            onChange={(v) => set('estimatedReadyDateTime', localInputToIso(v, stop.estimatedReadyDateTime || original?.estimatedReadyDateTime))}
            readOnly={readOnly} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <EditField label="Close" type="datetime-local"
            value={isoToLocalInput(stop.estimatedCloseDateTime)}
            original={isoToLocalInput(original?.estimatedCloseDateTime)}
            onChange={(v) => set('estimatedCloseDateTime', localInputToIso(v, stop.estimatedCloseDateTime || original?.estimatedCloseDateTime))}
            readOnly={readOnly} />
        </Grid>
        <Grid item xs={12}>
          <EditField label="Instructions" value={stop.instructions} original={original?.instructions}
            onChange={(v) => set('instructions', v)} multiline rows={2} readOnly={readOnly} />
        </Grid>
      </Grid>
    </Box>
  );
}

function CommodityEditor({ commodity, original, onChange, onRemove, index, readOnly = false }) {
  const set = (k, v) => onChange({ ...commodity, [k]: v });
  const numSet = (k) => (v) => set(k, v === '' ? null : Number(v));
  return (
    <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, minWidth: 0 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="overline" sx={{ fontWeight: 700 }}>Commodity #{index + 1}</Typography>
        {!readOnly && <IconButton size="small" onClick={onRemove} color="error"><DeleteIcon fontSize="small" /></IconButton>}
      </Stack>
      <Grid container spacing={1.5}>
        <Grid item xs={6} sm={3}>
          <EditField label="Handling qty" type="number" value={commodity.handlingQuantity} original={original?.handlingQuantity}
            onChange={numSet('handlingQuantity')} readOnly={readOnly} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField select size="small" fullWidth label="Packaging type" value={commodity.packagingType || 'Pallet'}
            onChange={readOnly ? undefined : (e) => set('packagingType', e.target.value)} disabled={readOnly}
            sx={readOnly ? READ_ONLY_SX : undefined}>
            {PACKAGING_TYPES.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={6} sm={3}>
          <EditField label="Pieces total" type="number" value={commodity.piecesTotal} original={original?.piecesTotal}
            onChange={numSet('piecesTotal')} readOnly={readOnly} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField select size="small" fullWidth label="Freight class" value={commodity.freightClass || 'No Class'}
            onChange={readOnly ? undefined : (ev) => set('freightClass', ev.target.value)} disabled={readOnly}
            sx={readOnly ? READ_ONLY_SX : undefined}>
            {FREIGHT_CLASSES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            {commodity.freightClass && !FREIGHT_CLASSES.includes(commodity.freightClass) && (
              <MenuItem value={commodity.freightClass}>{commodity.freightClass}</MenuItem>
            )}
          </TextField>
        </Grid>
        <Grid item xs={6} sm={3}>
          <EditField label="Length (in)" type="number" value={commodity.length} original={original?.length} onChange={numSet('length')} readOnly={readOnly} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <EditField label="Width (in)"  type="number" value={commodity.width}  original={original?.width}  onChange={numSet('width')}  readOnly={readOnly} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <EditField label="Height (in)" type="number" value={commodity.height} original={original?.height} onChange={numSet('height')} readOnly={readOnly} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <EditField label="Weight (lb)" type="number" value={commodity.weightTotal} original={original?.weightTotal} onChange={numSet('weightTotal')} readOnly={readOnly} />
        </Grid>
        <Grid item xs={12}>
          <EditField label="Description" value={commodity.description} original={original?.description}
            onChange={(v) => set('description', v)} multiline rows={2} readOnly={readOnly} />
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel control={
            <Checkbox checked={!!commodity.hazardousMaterial} disabled={readOnly}
              onChange={readOnly ? undefined : (e) => set('hazardousMaterial', e.target.checked)} />
          } label="Hazardous material" />
        </Grid>
      </Grid>
    </Box>
  );
}

function ReferencesEditor({ refs, originalRefs, onChange, readOnly = false }) {
  const update = (i, partial) => {
    const copy = refs.slice();
    copy[i] = { ...copy[i], ...partial };
    onChange(copy);
  };
  const add = () => onChange([...(refs || []), { referenceType: 'Customer PO Number', value: '' }]);
  const remove = (i) => onChange(refs.filter((_, j) => j !== i));

  return (
    <Box sx={{ minWidth: 0 }}>
      <Stack spacing={1}>
        {(refs || []).map((r, i) => {
          const orig = (originalRefs || [])[i];
          const dirty = !readOnly && (!orig || orig.referenceType !== r.referenceType || (orig.value || '') !== (r.value || ''));
          return (
            <Grid container spacing={1} key={i} alignItems="center">
              <Grid item xs={12} sm={5}>
                <TextField select size="small" fullWidth label="Type" value={r.referenceType || ''}
                  onChange={readOnly ? undefined : (e) => update(i, { referenceType: e.target.value })}
                  disabled={readOnly}
                  sx={readOnly ? READ_ONLY_SX : undefined}>
                  {COMMON_REF_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                  {!COMMON_REF_TYPES.includes(r.referenceType) && r.referenceType && (
                    <MenuItem value={r.referenceType}>{r.referenceType}</MenuItem>
                  )}
                </TextField>
              </Grid>
              <Grid item xs={readOnly ? 12 : 10} sm={readOnly ? 7 : 6}>
                <TextField size="small" label="Value" value={r.value || ''} fullWidth
                  onChange={readOnly ? undefined : (e) => update(i, { value: e.target.value })}
                  InputProps={readOnly ? { readOnly: true } : undefined}
                  sx={readOnly ? READ_ONLY_SX : { '& .MuiInputBase-root': dirty ? { bgcolor: 'warning.50' } : {} }} />
              </Grid>
              {!readOnly && (
                <Grid item xs={2} sm={1} sx={{ textAlign: 'right' }}>
                  <IconButton size="small" color="error" onClick={() => remove(i)}><DeleteIcon fontSize="small" /></IconButton>
                </Grid>
              )}
            </Grid>
          );
        })}
      </Stack>
      {!readOnly && <Button size="small" startIcon={<AddIcon />} onClick={add} sx={{ mt: 1 }}>Add reference</Button>}
    </Box>
  );
}

function SpacexDetail({ jobId, onBack, onRefresh }) {
  const { getAccessTokenSilently } = useAuth0();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [attachmentUrl, setAttachmentUrl] = useState(null);   // blob: URL for raw file (PDF embeds)
  const [previewUrl, setPreviewUrl] = useState(null);         // blob: URL for HTML preview (XLSX)
  const [edited, setEdited] = useState(null);                 // editable working copy of mapped_payload
  const [originalBody, setOriginalBody] = useState(null);     // immutable original
  const [submitting, setSubmitting] = useState(false);        // user clicked Submit; waiting on worker → TAI

  const token = useCallback(async () => getAccessTokenSilently({
    audience: process.env.REACT_APP_AUTH0AUDIENCE,
    scope: process.env.REACT_APP_AUTH0SCOPE,
  }), [getAccessTokenSilently]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const t = await token();
      const r = await api.get(`${BASE}/api/spacex/jobs/${jobId}`, { headers: { Authorization: `Bearer ${t}` } });
      setData(r.data);
      const body = r.data?.job?.mapped_payload || null;
      setOriginalBody(body ? JSON.parse(JSON.stringify(body)) : null);
      // If the operator already saved edits (e.g. they submitted, TAI rejected, status went to 'failed'),
      // restore those edits to the working form so they don't have to retype.
      const overrides = r.data?.job?.operator_overrides || null;
      const starting = overrides && typeof overrides === 'object'
        ? { ...(body || {}), ...overrides }
        : body;
      setEdited(starting ? JSON.parse(JSON.stringify(starting)) : null);
    } catch (e) {
      toast.error('Failed to load job: ' + (e.response?.data?.error || e.response?.data || e.message));
    } finally {
      setLoading(false);
    }
  }, [jobId, token]);

  useEffect(() => { load(); }, [load]);

  // Fetch the original attachment (for PDF iframe + XLSX download button)
  useEffect(() => {
    let active = true;
    let url = null;
    (async () => {
      try {
        const t = await token();
        const r = await api.get(`${BASE}/api/spacex/jobs/${jobId}/attachment`, {
          headers: { Authorization: `Bearer ${t}` },
          responseType: 'blob',
        });
        if (!active) return;
        url = URL.createObjectURL(r.data);
        setAttachmentUrl(url);
      } catch (e) { /* swallow */ }
    })();
    return () => { active = false; if (url) URL.revokeObjectURL(url); };
  }, [jobId, token]);

  // For XLSX: fetch the server-rendered HTML preview and embed it
  useEffect(() => {
    if (!data?.job || data.job.file_type !== 'xlsx') return;
    let active = true;
    let url = null;
    (async () => {
      try {
        const t = await token();
        const r = await api.get(`${BASE}/api/spacex/jobs/${jobId}/preview`, {
          headers: { Authorization: `Bearer ${t}` },
          responseType: 'blob',
        });
        if (!active) return;
        url = URL.createObjectURL(new Blob([r.data], { type: 'text/html' }));
        setPreviewUrl(url);
      } catch (e) { /* swallow — preview is best-effort */ }
    })();
    return () => { active = false; if (url) URL.revokeObjectURL(url); };
  }, [jobId, token, data?.job?.file_type]);

  async function act(action, body) {
    setBusy(true);
    try {
      const t = await token();
      await api.post(`${BASE}/api/spacex/jobs/${jobId}/${action}`, body || {}, {
        headers: { Authorization: `Bearer ${t}` },
      });
      toast.success(`Queued: ${action}`);
      setTimeout(() => { load(); onRefresh && onRefresh(); }, 1500);
    } catch (e) {
      toast.error(`${action} failed: ` + (e.response?.data?.error || e.response?.data || e.message));
    } finally {
      setBusy(false);
    }
  }

  // Submit + poll until the worker drives the job to a terminal state.
  // While this is running, the form is locked and a "Submitting…" banner shows.
  async function pollUntilTerminal(maxSeconds = 45) {
    const terminal = new Set(['submitted', 'failed', 'dismissed', 'dismissed_duplicate']);
    const start = Date.now();
    while (Date.now() - start < maxSeconds * 1000) {
      await new Promise((r) => setTimeout(r, 1000));
      try {
        const t = await token();
        const r = await api.get(`${BASE}/api/spacex/jobs/${jobId}`, { headers: { Authorization: `Bearer ${t}` } });
        const status = r.data?.job?.status;
        const pending = r.data?.job?.pending_action;
        // Sync the UI with the latest server state every tick (status chip etc.)
        setData(r.data);
        if (terminal.has(status) && !pending) {
          // Refresh edited from server (may include operator_overrides that worker recorded)
          const body = r.data?.job?.mapped_payload || null;
          const overrides = r.data?.job?.operator_overrides || null;
          setOriginalBody(body ? JSON.parse(JSON.stringify(body)) : null);
          const starting = overrides && typeof overrides === 'object'
            ? { ...(body || {}), ...overrides }
            : body;
          setEdited(starting ? JSON.parse(JSON.stringify(starting)) : null);
          return r.data?.job;
        }
      } catch { /* keep polling */ }
    }
    return null; // timeout — caller can show a soft warning
  }

  const isDirty = useMemo(() => {
    if (!edited || !originalBody) return false;
    return JSON.stringify(edited) !== JSON.stringify(originalBody);
  }, [edited, originalBody]);

  async function submit() {
    if (!edited) return;
    setSubmitting(true);
    try {
      const t = await token();
      await api.post(`${BASE}/api/spacex/jobs/${jobId}/submit`, { overrides: edited }, {
        headers: { Authorization: `Bearer ${t}` },
      });
      const finalJob = await pollUntilTerminal(45);
      if (!finalJob) {
        toast.warning('Submit is taking longer than expected. The worker is still processing — refresh in a moment.');
      } else if (finalJob.status === 'submitted') {
        toast.success(`Submitted to TAI as #${finalJob.tai_shipment_id}`);
      } else if (finalJob.status === 'failed') {
        toast.error('TAI rejected: ' + (finalJob.error_detail || finalJob.error_code || 'unknown'));
      }
      onRefresh && onRefresh();
    } catch (e) {
      toast.error('submit failed: ' + (e.response?.data?.error || e.response?.data || e.message));
    } finally {
      setSubmitting(false);
    }
  }
  function resetEdits() {
    setEdited(originalBody ? JSON.parse(JSON.stringify(originalBody)) : null);
  }

  if (loading || !data) return <Box sx={{ p: 3, width: '100%', textAlign: 'center' }}><CircularProgress /></Box>;
  const { job, events } = data;
  const reasons = job.needs_review_reasons || [];
  const isXlsx = job.file_type === 'xlsx';
  const isPdf  = job.file_type === 'pdf';
  const isClosed  = ['submitted','dismissed','dismissed_duplicate'].includes(job.status);
  // In-flight = user clicked submit and we're polling for the terminal state. Locks the form
  // and hides edit-related affordances so intermediate states (like 'mapped') don't confuse.
  const inFlight = submitting || (!!job.pending_action) || job.status === 'mapped';
  // readOnly view for closed jobs OR in-flight: no editable inputs, no dirty state, no action buttons.
  const readOnly = isClosed || inFlight;
  const canSubmit = !job.tai_shipment_id && !readOnly &&
    ['needs_review','failed','submit_uncertain','parsed','received'].includes(job.status);

  // Header — sticky. Behavior depends on whether the job is closed (submitted/dismissed) or open.
  const Header = (
    <Stack direction="row" alignItems="center" spacing={1} useFlexGap flexWrap="wrap"
           sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
      <IconButton onClick={onBack} size="small"><ArrowBackIcon /></IconButton>
      <Typography variant="h6" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        Job #{job.job_id} — {job.file_name}
      </Typography>
      <StatusChip status={job.status} />
      {/* Open jobs only: dirty indicator + edit/reset/submit/retry/dismiss controls */}
      {!readOnly && isDirty && <Chip size="small" color="warning" label="Unsaved edits" />}
      {job.tai_shipment_id && (
        <Button startIcon={<OpenInNewIcon />}
                href={`https://freightsaver.taicloud.net/back-office/shipment/shipment-details/${job.tai_shipment_id}`}
                target="_blank" size="small">
          TAI #{job.tai_shipment_id}
        </Button>
      )}
      {!readOnly && isDirty && (
        <Button size="small" disabled={busy} onClick={resetEdits} startIcon={<RestartAltIcon />}>Reset edits</Button>
      )}
      {canSubmit && (
        <Button size="small" color="success" variant="contained" disabled={busy} onClick={submit} startIcon={<CheckCircleIcon />}>
          {isDirty ? 'Submit edits to TAI' : 'Submit to TAI'}
        </Button>
      )}
      {!readOnly && (
        <>
          <Tooltip title="Re-parse the original file from scratch (discards any edits)">
            <span><Button size="small" disabled={busy} onClick={() => act('retry')} startIcon={<RefreshIcon />}>Retry parse</Button></span>
          </Tooltip>
          <Button size="small" color="warning" disabled={busy} onClick={() => act('dismiss', { reason: 'manual' })} startIcon={<CancelIcon />}>Dismiss</Button>
        </>
      )}
    </Stack>
  );

  // Review-reasons banner only matters for OPEN jobs that need attention. Hide on closed jobs.
  const ReasonsBanner = !readOnly && reasons.length > 0 && (
    <Box sx={{ px: 2, py: 1, bgcolor: 'warning.light', borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <ErrorIcon fontSize="small" />
        <Typography variant="body2" sx={{ fontWeight: 600 }}>Review reasons:</Typography>
        {reasons.map((r, i) => <Chip key={i} size="small" label={r} variant="outlined" />)}
      </Stack>
    </Box>
  );

  // In-flight banner — user clicked submit, worker is processing.
  const InFlightBanner = inFlight && !isClosed && (
    <Box sx={{
      px: 2, py: 1.5, flexShrink: 0, bgcolor: 'info.light', color: 'info.contrastText',
      borderBottom: '1px solid', borderColor: 'divider',
    }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <CircularProgress size={18} sx={{ color: 'inherit' }} />
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Submitting to TAI… (status: {job.status}{job.pending_action ? ` · ${job.pending_action} queued` : ''})
        </Typography>
      </Stack>
    </Box>
  );

  // Success banner for closed jobs.
  const ClosedBanner = isClosed && (
    <Box sx={{
      px: 2, py: 1.5, flexShrink: 0,
      bgcolor: job.status === 'submitted' ? 'success.light' : 'grey.200',
      borderBottom: '1px solid', borderColor: 'divider',
      color: job.status === 'submitted' ? 'success.contrastText' : 'text.primary',
    }}>
      <Stack direction="row" spacing={1} alignItems="center">
        {job.status === 'submitted' ? <CheckCircleIcon fontSize="small" /> : <CancelIcon fontSize="small" />}
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {job.status === 'submitted'
            ? `Submitted to TAI as shipment #${job.tai_shipment_id}. This job is closed (read-only).`
            : `Dismissed${job.error_detail ? `: ${job.error_detail}` : ''}.`}
        </Typography>
      </Stack>
    </Box>
  );

  // The editable working copy. Each setter merges back into `edited`.
  const e = edited || {};
  const setTop = (k, v) => setEdited({ ...e, [k]: v });
  const setStop = (i) => (newStop) => {
    const stops = (e.stops || []).slice();
    stops[i] = newStop;
    setEdited({ ...e, stops });
  };
  const setCommodity = (i) => (newC) => {
    const list = (e.commodities || []).slice();
    list[i] = newC;
    setEdited({ ...e, commodities: list });
  };
  const removeCommodity = (i) => {
    const list = (e.commodities || []).filter((_, j) => j !== i);
    // Also drop the corresponding pickup/delivery commodity links
    const stops = (e.stops || []).map((s) => ({
      ...s,
      shipmentStopPickupCommodities: (s.shipmentStopPickupCommodities || []).filter((c) => c.shipmentCommodityId !== (i + 1)),
      shipmentStopDeliveryCommodities: (s.shipmentStopDeliveryCommodities || []).filter((c) => c.shipmentCommodityId !== (i + 1)),
    }));
    setEdited({ ...e, commodities: list, stops });
  };
  const addCommodity = () => {
    const nextId = ((e.commodities || []).length || 0) + 1;
    const newC = {
      shipmentCommodityId: nextId, handlingQuantity: 1, packagingType: 'Pallet',
      length: 0, width: 0, height: 0, weightTotal: 0, piecesTotal: 1,
      freightClass: 'No Class', description: '', hazardousMaterial: false,
    };
    const list = [...(e.commodities || []), newC];
    const stops = (e.stops || []).map((s, i) => ({
      ...s,
      ...(i === 0 ? { shipmentStopPickupCommodities:   [...(s.shipmentStopPickupCommodities   || []), { shipmentCommodityId: nextId }] } : {}),
      ...(i === (e.stops?.length || 0) - 1 ? { shipmentStopDeliveryCommodities: [...(s.shipmentStopDeliveryCommodities || []), { shipmentCommodityId: nextId }] } : {}),
    }));
    setEdited({ ...e, commodities: list, stops });
  };
  const setRefs = (refs) => setEdited({ ...e, shipmentReferenceNumbers: refs });

  const orig = originalBody || {};

  // The right-pane content (constrained to its container; never overflows horizontally)
  const DetailBody = (
    <Stack spacing={4} sx={{ p: { xs: 1.5, md: 2 }, width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }}>
      {/* Email summary (read-only) */}
      <Box>
        <Typography variant="overline" color="text.secondary">Email</Typography>
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} md={6}>
            <Typography variant="body2"><b>From:</b> {job.from_address || '—'}</Typography>
            <Typography variant="body2"><b>Subject:</b> {job.subject || '—'}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2"><b>Received:</b> {new Date(job.received_at).toLocaleString()}</Typography>
            <Typography variant="body2"><b>Confidence:</b> {job.confidence == null ? '—' : (job.confidence * 100).toFixed(0) + '%'}</Typography>
          </Grid>
          {job.error_code && (
            <Grid item xs={12}>
              <Typography variant="body2" color="error"><b>Error:</b> {job.error_code}{job.error_detail ? ' — ' + job.error_detail : ''}</Typography>
            </Grid>
          )}
        </Grid>
      </Box>

      {/* Shipment-level */}
      <Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="overline" color="text.secondary">Shipment</Typography>
          <Chip size="small" variant="outlined" label={`customerId ${e.customerId ?? '—'}`} />
        </Stack>
        <Grid container spacing={1.5}>
          <Grid item xs={6} sm={4} md={3}>
            <TextField select size="small" fullWidth label="Type" value={e.shipmentType || 'Truckload'}
              onChange={readOnly ? undefined : (ev) => setTop('shipmentType', ev.target.value)} disabled={readOnly}
              sx={readOnly ? READ_ONLY_SX : undefined}>
              {SHIPMENT_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              {e.shipmentType && !SHIPMENT_TYPES.includes(e.shipmentType) && <MenuItem value={e.shipmentType}>{e.shipmentType}</MenuItem>}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={4} md={3}>
            <TextField select size="small" fullWidth label="Service level" value={e.serviceLevel || 'Standard'}
              onChange={readOnly ? undefined : (ev) => setTop('serviceLevel', ev.target.value)} disabled={readOnly}
              sx={readOnly ? READ_ONLY_SX : undefined}>
              {SERVICE_LEVELS.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              {e.serviceLevel && !SERVICE_LEVELS.includes(e.serviceLevel) && <MenuItem value={e.serviceLevel}>{e.serviceLevel}</MenuItem>}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4} md={6}>
            <TextField select size="small" fullWidth label="Trailer type" value={e.trailerType || ''}
              onChange={readOnly ? undefined : (ev) => setTop('trailerType', ev.target.value)} disabled={readOnly}
              sx={readOnly ? READ_ONLY_SX : undefined}>
              <MenuItem value="">(none)</MenuItem>
              {TRAILER_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              {e.trailerType && !TRAILER_TYPES.includes(e.trailerType) && <MenuItem value={e.trailerType}>{e.trailerType}</MenuItem>}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField select size="small" fullWidth label="Trailer size" value={e.trailerSize || ''}
              onChange={readOnly ? undefined : (ev) => setTop('trailerSize', ev.target.value)} disabled={readOnly}
              sx={readOnly ? READ_ONLY_SX : undefined}>
              <MenuItem value="">(none)</MenuItem>
              {TRAILER_SIZES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField select size="small" fullWidth label="Weight units" value={e.weightUnits || 'lbs'}
              onChange={readOnly ? undefined : (ev) => setTop('weightUnits', ev.target.value)} disabled={readOnly}
              sx={readOnly ? READ_ONLY_SX : undefined}>
              {WEIGHT_UNITS.map((u) => <MenuItem key={u} value={u}>{u}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField select size="small" fullWidth label="Dimension units" value={e.dimensionUnits || 'in'}
              onChange={readOnly ? undefined : (ev) => setTop('dimensionUnits', ev.target.value)} disabled={readOnly}
              sx={readOnly ? READ_ONLY_SX : undefined}>
              {DIMENSION_UNITS.map((u) => <MenuItem key={u} value={u}>{u}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={3}>
            <FormControlLabel control={<Checkbox checked={!!e.stackable} disabled={readOnly}
              onChange={readOnly ? undefined : (ev) => setTop('stackable', ev.target.checked)} />} label="Stackable" />
          </Grid>
        </Grid>
      </Box>

      {/* Stops */}
      <Box>
        <Typography variant="overline" color="text.secondary">Stops ({(e.stops || []).length})</Typography>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {(e.stops || []).map((s, i) => (
            <StopEditor key={i} stop={s} original={(orig.stops || [])[i]} title={s.stopType || 'Stop'}
              onChange={setStop(i)} readOnly={readOnly} />
          ))}
        </Stack>
      </Box>

      {/* Commodities */}
      <Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="overline" color="text.secondary">Commodities ({(e.commodities || []).length})</Typography>
          {!readOnly && <Button size="small" startIcon={<AddIcon />} onClick={addCommodity}>Add commodity</Button>}
        </Stack>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {(e.commodities || []).map((c, i) => (
            <CommodityEditor key={i} commodity={c} original={(orig.commodities || [])[i]}
              onChange={setCommodity(i)} onRemove={() => removeCommodity(i)} index={i} readOnly={readOnly} />
          ))}
        </Stack>
      </Box>

      {/* References */}
      <Box>
        <Typography variant="overline" color="text.secondary">References</Typography>
        <Box sx={{ mt: 1 }}>
          <ReferencesEditor refs={e.shipmentReferenceNumbers || []} originalRefs={orig.shipmentReferenceNumbers || []}
            onChange={setRefs} readOnly={readOnly} />
        </Box>
      </Box>

      {/* Event timeline */}
      <Box>
        <Typography variant="overline" color="text.secondary">Event Timeline ({events?.length || 0})</Typography>
        <Stack spacing={0.5} sx={{ mt: 1 }}>
          {(events || []).map((ev) => (
            <Box key={ev.event_id} sx={{ borderLeft: '3px solid', borderColor: 'primary.light', pl: 1.5, py: 0.25 }}>
              <Typography variant="caption" color="text.secondary">{new Date(ev.event_at).toLocaleString()} · {ev.actor}</Typography>
              <Typography variant="body2">
                <b>{ev.event_type}</b>
                {ev.payload && Object.keys(ev.payload || {}).length > 0 && (
                  <span style={{ color: '#777', marginLeft: 8 }}>{JSON.stringify(ev.payload).slice(0, 200)}</span>
                )}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Box>

      {/* Original (download) */}
      <Box>
        <Typography variant="overline" color="text.secondary">Original Attachment</Typography>
        <Box sx={{ mt: 1 }}>
          {attachmentUrl ? (
            <Button startIcon={<OpenInNewIcon />} variant="outlined" href={attachmentUrl} target="_blank" download={job.file_name} size="small">
              Download {job.file_type?.toUpperCase()}
            </Button>
          ) : (
            <CircularProgress size={20} />
          )}
        </Box>
      </Box>

      {/* Full payload */}
      <Box>
        <Typography variant="overline" color="text.secondary">
          {readOnly ? 'Full TAI request payload (submitted)' : 'Full TAI request payload (current edits)'}
        </Typography>
        <Box sx={{ mt: 1 }}><PrettyJSON data={edited} /></Box>
      </Box>
    </Stack>
  );

  // Pick the right preview source — PDF embeds the raw file, XLSX embeds our HTML render
  const previewSrc = isPdf ? attachmentUrl : (isXlsx ? previewUrl : null);
  const previewReady = !!previewSrc;

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, width: '100%' }}>
      {Header}
      {ReasonsBanner}
      {InFlightBanner}
      {ClosedBanner}

      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', overflow: 'hidden' }}>
        {/* Preview pane — narrower (40%) since the edit pane is denser */}
        <Box sx={{ flex: '0 0 40%', borderRight: '1px solid', borderColor: 'divider', minWidth: 0, bgcolor: 'grey.50' }}>
          {previewReady ? (
            <iframe src={previewSrc} title="Original" style={{ width: '100%', height: '100%', border: 0, background: 'white' }} />
          ) : (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <CircularProgress size={20} />
              <Typography variant="caption" display="block" sx={{ mt: 1 }} color="text.secondary">
                Rendering {job.file_type?.toUpperCase()} preview…
              </Typography>
            </Box>
          )}
        </Box>
        {/* Edit pane — wider (60%), scrollable */}
        <Box sx={{ flex: '1 1 60%', minWidth: 0, overflowY: 'auto', overflowX: 'hidden' }}>
          {DetailBody}
        </Box>
      </Box>
    </Box>
  );
}

export default function Spacex({ user }) {
  const [openJobId, setOpenJobId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, width: '100%' }}>
      {openJobId != null ? (
        <SpacexDetail jobId={openJobId} onBack={() => { setOpenJobId(null); refresh(); }} onRefresh={refresh} />
      ) : (
        <SpacexQueue onOpen={(id) => setOpenJobId(id)} refreshKey={refreshKey} />
      )}
    </Box>
  );
}
